import { Link } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'
import { mdiArrowRight, mdiSourceBranch } from '@mdi/js'
import { AppIcon } from './ui/icon'
import { useContract } from '../hooks/useQueries'
import { cn } from '../lib/utils'

// ContractChainStrip renders the avenant chain around a contract as a compact,
// left-to-right timeline: predecessor → current → successor. Each node shows the
// member's printed reference (or name) and its own period (start → end), so a
// multi-avenant history reads as a sequence of periods. Neighbours are links —
// each chain member keeps its own attached documents on its own page.
//
// It renders nothing when the contract is neither preceded nor followed, so a
// standalone contract shows no chrome.

interface ChainNodeData {
  uid?: string
  code?: string
  name?: string
  start_date?: string | null
  end_date?: string | null
}

interface Props {
  org: string
  current: ChainNodeData
}

export function ContractChainStrip({ org, current }: Props) {
  const { t } = useTranslation('contracts')
  const prevUid = (current as { previous_contract_uid?: string }).previous_contract_uid
  const nextUid = (current as { next_contract_uid?: string }).next_contract_uid

  // Cheap, cached single-row fetches — only enabled when a neighbour exists.
  const { data: prev } = useContract(org, prevUid || '', [])
  const { data: next } = useContract(org, nextUid || '', [])

  if (!prevUid && !nextUid) return null

  const period = (c?: ChainNodeData) => {
    const dash = '—'
    const s = c?.start_date || dash
    const e = c?.end_date || dash
    return `${s} → ${e}`
  }

  const nodeLabel = (c?: ChainNodeData, fallbackUid?: string) =>
    c?.code || c?.name || (fallbackUid ? fallbackUid.slice(0, 8) : dashLabel())

  function dashLabel() {
    return '…'
  }

  const nodeBody = (c: ChainNodeData | undefined, fallbackUid: string | undefined, isCurrent: boolean, testid: string) => (
    <div
      data-testid={testid}
      className={cn(
        'flex flex-col rounded-md border px-3 py-1.5 min-w-0',
        isCurrent
          ? 'border-primary bg-primary/5 ring-1 ring-primary/40'
          : 'border-border bg-background hover:border-primary/60 hover:bg-accent transition-colors',
      )}
    >
      <span className="text-sm font-medium text-foreground truncate">
        {nodeLabel(c, fallbackUid)}
      </span>
      <span className="text-xs text-muted-foreground tabular-nums">{period(c)}</span>
    </div>
  )

  const arrow = (
    <AppIcon path={mdiArrowRight} size="sm" className="shrink-0" aria-hidden="true" />
  )

  return (
    <div
      data-testid="contract-chain-strip"
      className="mt-3 flex items-center gap-2 overflow-x-auto"
      aria-label={t('detail.chain_label')}
    >
      <AppIcon path={mdiSourceBranch} size="sm" className="shrink-0" aria-hidden="true" />
      {prevUid && (
        <>
          <Link
            to="/$org/contracts/$contractUid"
            params={{ org, contractUid: prevUid }}
            data-testid="contract-chain-prev-link"
            title={t('detail.previous_contract')}
          >
            {nodeBody(prev, prevUid, false, 'contract-chain-prev')}
          </Link>
          {arrow}
        </>
      )}

      {nodeBody(current, current.uid, true, 'contract-chain-current')}

      {nextUid && (
        <>
          {arrow}
          <Link
            to="/$org/contracts/$contractUid"
            params={{ org, contractUid: nextUid }}
            data-testid="contract-chain-next-link"
            title={t('detail.next_contract')}
          >
            {nodeBody(next, nextUid, false, 'contract-chain-next')}
          </Link>
        </>
      )}
    </div>
  )
}
